// React and MUI imports for layout, theming, and UI components
import React from 'react';
import {
  Box, // MUI layout component
  Typography, // MUI text component
  Container, // MUI container for responsive width
  useMediaQuery, // MUI hook for responsive design
  useTheme, // MUI hook for theme access
  CssBaseline // MUI component to normalize CSS
} from '@mui/material';
import Header from '../../commons/Header'; // App header
import Footer from '../../commons/Footer'; // App footer
import HDButton from '../../hd-ui-kit/HDButton'; // Custom button
import { useNavigate } from 'react-router'; // React Router navigation
import {
  reactGAEvents, // Google Analytics event function
  backgroundTopRight, // Light theme background style
  backgroundTopRightDark, // Dark theme background style
  titleStyle, // Function for title text style
  styledText, // Function for body text style
  styledSignInBtn, // Function for sign-in button style
  styledBoxMainWrapper, // Function for main wrapper style
  styledDividerLeft // Function for divider style
} from '../../commons/commonFunctions';

/**
 * AccountActivation
 * This page is shown after a user signs up, instructing them to activate their account via an email link.
 * It provides a message and a button to return to the sign-in screen.
 */
const AccountActivation = () => {
  // Access the current MUI theme object
  const theme = useTheme();
  // Determine if the screen is mobile size
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // React Router navigation function
  const navigate = useNavigate();
  // Current theme mode ('light' or 'dark')
  const themeMode = theme.palette.mode;

  // Style for the main vertical flex box containing the title and instructions
  const MainStyledBox = {
    display: 'flex',
    flexDirection: 'column',
  };

  // Style for the wrapper around the sign-in button
  const StyledForgetWrapper = {
    textAlign: 'left',
    marginTop: '20px',
  };

  // Choose the background style based on the current theme
  const backColor = (theme.palette.mode === 'light')
    ? { ...backgroundTopRight }
    : { ...backgroundTopRightDark };

  /**
   * Handler for the 'Return to sign-in screen' button.
   * Triggers a Google Analytics event and navigates to the login page.
   */
  const handleOnClick = () => {
    reactGAEvents('account-activation', 'return-to-sign-in');
    navigate('/login');
  };

  return (
    <>
      {/* CssBaseline normalizes browser styles for consistency */}
      <CssBaseline />
      {/* App header (logo, nav, etc.) */}
      <Header />
      {/* Main background section with theme-based color/gradient */}
      <Box sx={{ ...backColor, display: 'grid' }}>
        {/* Main wrapper for content, responsive to mobile/desktop */}
        <Box sx={styledBoxMainWrapper(isMobile, themeMode)}>
          <Container maxWidth="lg">
            {/* Optional left divider for layout aesthetics */}
            <Box sx={styledDividerLeft(isMobile, theme)}>
              <Box>
                {/* Main content: title and instructions for activation */}
                <Box sx={MainStyledBox}>
                  {/* Title message for the user */}
                  <Typography variant="h2" sx={titleStyle(isMobile, themeMode)}>
                    Please click the activation link we sent to your email
                  </Typography>
                  {/* Instructional text for activating the account */}
                  <Typography variant="h2" sx={styledText(themeMode)}>
                    In order to activate your account and complete your profile, please click the link
                    sent to your email.
                  </Typography>
                </Box>
                {/* Section containing the button to return to sign-in */}
                <Box sx={StyledForgetWrapper}>
                  <Box>
                    <Box>
                      {/* Button styled according to theme, triggers handleOnClick */}
                      <HDButton
                        sx={styledSignInBtn(themeMode)}
                        variant={'text'}
                        text={'Return to sign-in screen'}
                        onClick={() => handleOnClick()}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
      {/* App footer (copyright, links, etc.) */}
      <Footer />
    </>
  );
};

export default AccountActivation;
